import moment from "moment";
import { sendMessageToParent } from "src/helpers/common";
import storage from "src/helpers/storage";
import { POST_MESSAGE_EVENTS, STORAGE_KEYS } from "src/constants/common";
import type { AppDispatch } from "src/redux/store";
import {
  getDailyLogsRequest,
  resetCgm,
  cgmHardSyncStarted,
  cgmGetLatestStarted,
  cgmMissingSyncStarted,
} from "src/redux/slices/cgmSlice";
import type { CgmState } from "src/types/cgm.types";
import { getCgmWarmupPriorityState } from "src/helpers/cgm";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type CgmStatusType =
  | "warmup"
  | "connected"
  | "disconnected"
  | "bluetooth_off"
  | "syncing"
  | "reconnecting"
  | "error"
  | "not_setup";

export type StatusBadgeType = "success" | "warning" | "danger" | "info";

export interface TransmitterStatus {
  label: string;
  type: StatusBadgeType;
}

export interface CgmStatusResult {
  status: CgmStatusType;
  transmitterStatus: TransmitterStatus;
  sensorStatus: string | null;
  isWarmup: boolean;
  warmupMinutesLeft?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CgmManager — Singleton class
// ─────────────────────────────────────────────────────────────────────────────

class CgmManager {
  private static instance: CgmManager;
  private dispatch: AppDispatch | null = null;

  private constructor() {}

  static getInstance(): CgmManager {
    if (!CgmManager.instance) {
      CgmManager.instance = new CgmManager();
    }
    return CgmManager.instance;
  }

  /**
   * Initialize with Redux dispatch. Call once at app root (e.g. CgmMessageListener or App).
   */
  init(dispatch: AppDispatch): void {
    this.dispatch = dispatch;
  }

  private ensureDispatch(): AppDispatch {
    if (!this.dispatch) {
      throw new Error("CgmManager not initialized. Call init(dispatch) first.");
    }
    return this.dispatch;
  }

  /**
   * Compute CGM status from Redux state. Pure — no side effects.
   * Warmup takes priority over connected — step/displayStatus checked first.
   */
  getStatus(cgm: CgmState): CgmStatusResult {
    const { step, displayStatus, isConnected, boundDevice, sensorStatusName } =
      cgm;

    const warmup = getCgmWarmupPriorityState(cgm);
    if (warmup.active) {
      const minutes = warmup.minutesLeft;
      return {
        status: "warmup",
        transmitterStatus: {
          label: minutes > 0 ? `Warmup (${minutes} min left)` : "Warmup",
          type: "warning",
        },
        sensorStatus: sensorStatusName,
        isWarmup: true,
        warmupMinutesLeft: minutes > 0 ? minutes : undefined,
      };
    }

    // Syncing: batch sync in progress
    if (
      cgm.cgmHardSync?.isLoading ||
      cgm.cgmGetLatest?.isLoading ||
      cgm.cgmMissingSync?.isLoading
    ) {
      return {
        status: "syncing",
        transmitterStatus: { label: "Syncing...", type: "info" },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    if (
      displayStatus === "BLUETOOTH_OFF" ||
      cgm.cgmConnectionLive?.connectionStatus === "BLUETOOTH_OFF"
    ) {
      return {
        status: "bluetooth_off",
        transmitterStatus: {
          label: "Bluetooth off",
          type: "warning",
        },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    if (isConnected || displayStatus === "CONNECTED") {
      return {
        status: "connected",
        transmitterStatus: { label: "Connected", type: "success" },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    if (displayStatus === "EXPIRED") {
      return {
        status: "not_setup",
        transmitterStatus: {
          label: "Sensor expired — connect new CGM",
          type: "info",
        },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    if (
      displayStatus === "TRANSMITTER_DISCONNECT" ||
      displayStatus === "TIMEOUT"
    ) {
      return {
        status: "disconnected",
        transmitterStatus: {
          label: "Transmitter disconnected",
          type: "danger",
        },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    if (displayStatus === "DISCONNECTED") {
      return {
        status: "disconnected",
        transmitterStatus: {
          label: "Disconnected",
          type: "warning",
        },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    if ((step === "connected" && boundDevice) || boundDevice) {
      return {
        status: "reconnecting",
        transmitterStatus: { label: "Reconnecting…", type: "warning" },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    if (step === "binding" || step === "scanning") {
      return {
        status: "syncing",
        transmitterStatus: { label: "Syncing…", type: "info" },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    if (step === "error") {
      return {
        status: "error",
        transmitterStatus: { label: "Error", type: "danger" },
        sensorStatus: sensorStatusName,
        isWarmup: false,
      };
    }

    return {
      status: "not_setup",
      transmitterStatus: { label: "New", type: "info" },
      sensorStatus: sensorStatusName,
      isWarmup: false,
    };
  }

  /**
   * Refetch CGM status from RN (CGM_CHECK_STATUS, CGM_RECONNECT) and daily logs from API.
   * Called from: ConnectedBottomSheet, CGM connection journey, InsightComponentGlucose.
   *
   * Skips CGM_RECONNECT when cgm indicates fresh connection (intro, no bound device)
   * to avoid "No bound device to reconnect" error.
   */
  refetchCgmStatus(selectedDate?: string, cgm?: CgmState | null): void {
    sendMessageToParent(POST_MESSAGE_EVENTS.CGM_CHECK_STATUS, {});
    const isFreshConnection = cgm && cgm.step === "intro" && !cgm.boundDevice;
    if (!isFreshConnection) {
      sendMessageToParent(POST_MESSAGE_EVENTS.CGM_RECONNECT, {});
    }
    this.ensureDispatch()(
      getDailyLogsRequest({
        startDate: selectedDate ?? moment().format("YYYY-MM-DD"),
      })
    );
  }

  /** @deprecated Use refetchCgmStatus. Kept for backward compatibility. */
  refetchLatestData(selectedDate?: string, cgm?: CgmState | null): void {
    this.refetchCgmStatus(selectedDate, cgm);
  }

  /**
   * Clear CGM connection and reset state. Sends CGM_UNBIND_DEVICE to RN.
   */
  clearAndRestartConnection(options?: {
    navigate?: (path: string) => void;
    connectionPath?: string;
  }): void {
    this.ensureDispatch()(resetCgm());
    storage.removeItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    sendMessageToParent(POST_MESSAGE_EVENTS.CGM_UNBIND_DEVICE, {});

    if (options?.navigate && options?.connectionPath) {
      options.navigate(options.connectionPath);
    }
  }

  /** Insights sync / toolbar: RN pulls latest window and POSTs readings. */
  getLatestReading(): void {
    this.ensureDispatch()(cgmGetLatestStarted());
    sendMessageToParent(POST_MESSAGE_EVENTS.CGM_GET_LATEST_READING, {});
  }

  /** Gap fill: RN GETs missing ranges, pulls each from device, POSTs batches. */
  syncMissingPoints(): void {
    this.ensureDispatch()(cgmMissingSyncStarted());
    sendMessageToParent(POST_MESSAGE_EVENTS.CGM_SYNC_MISSING_POINTS, {});
  }

  /**
   * Full history upload: native `getGlucoseDataByDeviceId` → POST batches.
   * Redux loading starts here; RN sends SUCCESS / ERROR only.
   */
  hardSync(): void {
    this.ensureDispatch()(cgmHardSyncStarted());
    sendMessageToParent(POST_MESSAGE_EVENTS.CGM_HARD_SYNC, {});
  }

  /** RN generates ZIP and uploads diagnostic CGM logs. */
  generateUploadLogs(): void {
    sendMessageToParent(POST_MESSAGE_EVENTS.CGM_GENERATE_LOGS, {});
  }

  /** Android: ask RN to open battery optimization settings for this app. */
  openAndroidBatterySettings(): void {
    sendMessageToParent(
      POST_MESSAGE_EVENTS.CGM_OPEN_ANDROID_BATTERY_SETTINGS,
      {}
    );
  }

  /**
   * Start listening to RN postMessages and optionally poll. Returns cleanup.
   * TODO: Wire in Phase 2 — use parseCgmMessage + handleCgmMessage from services/cgm.
   */
  startLiveDataListener(_options?: {
    pollIntervalMs?: number;
    selectedDate?: string;
  }): () => void {
    return () => {};
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Singleton export
// ─────────────────────────────────────────────────────────────────────────────

export const cgmManager = CgmManager.getInstance();
